import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Github, Linkedin, Mail, Phone, ExternalLink, ArrowRight, ArrowLeft, Sun, Moon, Filter, X, Code, Globe, Zap, Cpu, HardDrive, Briefcase, Calendar, MapPin, Dribbble, Palette, ArrowBigLeft, ArrowBigRight } from 'lucide-react';

// Case Study Component
const CaseStudy = ({ project, onBack, onNext, onPrevious, hasNext, hasPrevious, theme }) => {
  return (
    <div className={`min-h-screen ${theme === 'bubblegum' ? 'bg-pink-200' : 'bg-black'} text-white font-light transition-colors duration-1000`}>
      <style jsx>{`
        .case-study-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 120px 24px 80px;
        }
        .hero-image {
          width: 100%;
          height: 500px;
          object-fit: cover;
          border-radius: 20px;
          margin-bottom: 60px;
        }
        .section-title {
          font-size: 2.5rem;
          font-weight: 300;
          margin-bottom: 24px;
          color: ${theme === 'bubblegum' ? '#ff69b4' : '#ffffff'};
        }
        .content-text {
          line-height: 1.8;
          margin-bottom: 32px;
          color: ${theme === 'bubblegum' ? '#d65db1' : '#cccccc'};
        }
        .image-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 24px;
          margin: 48px 0;
        }
        .case-study-image {
          width: 100%;
          height: 250px;
          object-fit: cover;
          border-radius: 12px;
          transition: transform 0.3s ease;
        }
        .case-study-image:hover {
          transform: scale(1.02);
        }
        .tech-badge {
          display: inline-block;
          padding: 8px 16px;
          margin: 4px;
          background: ${theme === 'bubblegum' ? 'rgba(255, 105, 180, 0.2)' : 'rgba(255, 255, 255, 0.1)'};
          border: 1px solid ${theme === 'bubblegum' ? '#ff69b4' : '#ffffff'};
          border-radius: 20px;
          font-size: 14px;
        }
        .navigation-button {
          position: fixed;
          top: 50%;
          transform: translateY(-50%);
          padding: 16px;
          background: ${theme === 'bubblegum' ? '#ff69b4' : '#1a1a1a'};
          border: none;
          border-radius: 50%;
          cursor: pointer;
          transition: all 0.3s ease;
          z-index: 100;
        }
        .navigation-button:hover {
          background: ${theme === 'bubblegum' ? '#ff1493' : '#333333'};
          transform: translateY(-50%) scale(1.1);
        }
        .back-button {
          position: fixed;
          top: 30px;
          left: 30px;
          padding: 12px 24px;
          background: ${theme === 'bubblegum' ? '#ff69b4' : '#1a1a1a'};
          border: none;
          border-radius: 30px;
          cursor: pointer;
          transition: all 0.3s ease;
          z-index: 100;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .back-button:hover {
          background: ${theme === 'bubblegum' ? '#ff1493' : '#333333'};
          transform: scale(1.05);
        }
        .highlight-box {
          background: ${theme === 'bubblegum' ? 'rgba(255, 105, 180, 0.1)' : 'rgba(255, 255, 255, 0.05)'};
          border-left: 4px solid ${theme === 'bubblegum' ? '#ff69b4' : '#ffffff'};
          padding: 24px;
          margin: 32px 0;
          border-radius: 8px;
        }
      `}</style>

      {/* Back Button */}
      <button onClick={onBack} className="back-button">
        <ArrowBigLeft className="w-5 h-5" />
        <span>Back to Portfolio</span>
      </button>

      {/* Previous Case Study Button */}
      {hasPrevious && (
        <button onClick={onPrevious} className="navigation-button" style={{ left: '30px' }}>
          <ArrowBigLeft className="w-6 h-6" />
        </button>
      )}

      {/* Next Case Study Button */}
      {hasNext && (
        <button onClick={onNext} className="navigation-button" style={{ right: '30px' }}>
          <ArrowBigRight className="w-6 h-6" />
        </button>
      )}

      <div className="case-study-content">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className={`text-5xl md:text-6xl font-light mb-6 ${theme === 'bubblegum' ? 'text-pink-800' : 'text-white'}`}>
            {project.title}
          </h1>
          <p className={`text-xl ${theme === 'bubblegum' ? 'text-pink-700' : 'text-gray-300'} mb-8 max-w-3xl mx-auto`}>
            {project.subtitle}
          </p>
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {project.tech.map((tech, index) => (
              <span key={index} className="tech-badge">
                {tech}
              </span>
            ))}
          </div>
        </div>

        {/* Hero Image */}
        <img 
          src={project.caseStudy?.heroImage || project.imageUrl} 
          alt={project.title}
          className="hero-image"
          onError={(e) => {
            console.log('Image failed to load:', e.target.src);
            e.target.src = `https://via.placeholder.com/1200x500/ff69b4/ffffff?text=${encodeURIComponent(project.title)}`;
          }}
        />

        {/* Project Overview */}
        <section className="mb-16">
          <h2 className="section-title">Project Overview</h2>
          <p className="content-text">
            {project.caseStudy?.overview || project.description}
          </p>
        </section>

        {/* The Challenge */}
        <section className="mb-16">
          <h2 className="section-title">The Challenge</h2>
          <div className="highlight-box">
            <p className="content-text mb-0">
              {project.caseStudy?.challenge || "The project aimed to solve complex user experience challenges while maintaining technical excellence and scalability."}
            </p>
          </div>
        </section>

        {/* Design Process */}
        <section className="mb-16">
          <h2 className="section-title">Design Process</h2>
          <p className="content-text">
            {project.caseStudy?.process || "Following a user-centered design approach, I conducted extensive research, created wireframes, and iterated based on user feedback to ensure the final product met both user needs and business goals."}
          </p>
          
          {/* Process Images */}
          <div className="image-grid">
            {project.caseStudy?.processImages?.map((image, index) => (
              <img 
                key={index}
                src={image} 
                alt={`Design Process ${index + 1}`}
                className="case-study-image"
                onError={(e) => {
                  e.target.src = `https://via.placeholder.com/300x250/ff69b4/ffffff?text=Process+${index + 1}`;
                }}
              />
            )) || [
              <img 
                key="default1"
                src="/images/design-process-1.jpg" 
                alt="Design Process 1"
                className="case-study-image"
                onError={(e) => {
                  e.target.src = `https://via.placeholder.com/300x250/ff69b4/ffffff?text=Process+1`;
                }}
              />,
              <img 
                key="default2"
                src="/images/design-process-2.jpg" 
                alt="Design Process 2"
                className="case-study-image"
                onError={(e) => {
                  e.target.src = `https://via.placeholder.com/300x250/ff69b4/ffffff?text=Process+2`;
                }}
              />,
              <img 
                key="default3"
                src="/images/design-process-3.jpg" 
                alt="Design Process 3"
                className="case-study-image"
                onError={(e) => {
                  e.target.src = `https://via.placeholder.com/300x250/ff69b4/ffffff?text=Process+3`;
                }}
              />
            ]}
          </div>
        </section>

        {/* Key Features */}
        <section className="mb-16">
          <h2 className="section-title">Key Features</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {project.caseStudy?.features?.map((feature, index) => (
              <div key={index} className="highlight-box">
                <h3 className={`text-xl font-medium mb-3 ${theme === 'bubblegum' ? 'text-pink-800' : 'text-white'}`}>
                  {feature.title}
                </h3>
                <p className="content-text mb-0">
                  {feature.description}
                </p>
                {feature.image && (
                  <img 
                    src={feature.image} 
                    alt={feature.title}
                    className="w-full h-48 object-cover rounded-lg mt-4"
                    onError={(e) => {
                      e.target.src = `https://via.placeholder.com/400x200/ff69b4/ffffff?text=${encodeURIComponent(feature.title)}`;
                    }}
                  />
                )}
              </div>
            )) || project.highlights.map((highlight, index) => (
              <div key={index} className="highlight-box">
                <p className="content-text mb-0">
                  {highlight}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Results */}
        <section className="mb-16">
          <h2 className="section-title">Results & Impact</h2>
          <p className="content-text">
            {project.caseStudy?.results || "The project successfully achieved its goals, delivering significant improvements in user experience and engagement metrics. The solution has been well-received by users and stakeholders alike."}
          </p>
          
          {/* Results Images */}
          <div className="image-grid">
            {project.caseStudy?.resultsImages?.map((image, index) => (
              <img 
                key={index}
                src={image} 
                alt={`Results ${index + 1}`}
                className="case-study-image"
                onError={(e) => {
                  e.target.src = `https://via.placeholder.com/300x250/ff69b4/ffffff?text=Results+${index + 1}`;
                }}
              />
            )) || [
              <img 
                key="result1"
                src="/images/results-1.jpg" 
                alt="Results 1"
                className="case-study-image"
                onError={(e) => {
                  e.target.src = `https://via.placeholder.com/300x250/ff69b4/ffffff?text=Results+1`;
                }}
              />,
              <img 
                key="result2"
                src="/images/results-2.jpg" 
                alt="Results 2"
                className="case-study-image"
                onError={(e) => {
                  e.target.src = `https://via.placeholder.com/300x250/ff69b4/ffffff?text=Results+2`;
                }}
              />
            ]}
          </div>
        </section>

        {/* Lessons Learned */}
        <section className="mb-16">
          <h2 className="section-title">Lessons Learned</h2>
          <p className="content-text">
            {project.caseStudy?.lessons || "This project reinforced the importance of user research, iterative design, and cross-functional collaboration. It highlighted how thoughtful design can significantly impact user satisfaction and business outcomes."}
          </p>
        </section>

        {/* Navigation Footer */}
        <div className="flex justify-between items-center pt-16 border-t border-gray-700">
          <div>
            {hasPrevious && (
              <button onClick={onPrevious} className={`flex items-center gap-2 ${theme === 'bubblegum' ? 'text-pink-700' : 'text-gray-400'} hover:text-white transition-colors`}>
                <ArrowLeft className="w-5 h-5" />
                <span>Previous Case Study</span>
              </button>
            )}
          </div>
          <div>
            {hasNext && (
              <button onClick={onNext} className={`flex items-center gap-2 ${theme === 'bubblegum' ? 'text-pink-700' : 'text-gray-400'} hover:text-white transition-colors`}>
                <span>Next Case Study</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Portfolio = () => {
  const [activeSection, setActiveSection] = useState('home');
  const [isLoading, setIsLoading] = useState(true);
  const [currentProjectIndex, setCurrentProjectIndex] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [theme, setTheme] = useState('night');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [projectFilter, setProjectFilter] = useState('all');
  const [typedText, setTypedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [loopNum, setLoopNum] = useState(0);
  const [typingSpeed, setTypingSpeed] = useState(150);
  const [is3DView, setIs3DView] = useState(false);
  const [activeSkill, setActiveSkill] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showCaseStudy, setShowCaseStudy] = useState(false);
  const [currentCaseStudyIndex, setCurrentCaseStudyIndex] = useState(0);
  
  const containerRef = useRef(null);
  const touchStartX = useRef(0);
  
  // Check if mobile device
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);
  
  // Typewriter effect texts
  const texts = [
    "Ui/Ux Designer",
    "Full-Stack Developer",
    "Embedded Systems & Computer Vision Engineer"
  ];
  
  // Load theme preference
  useEffect(() => {
    const savedTheme = localStorage.getItem('portfolio-theme');
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    if (savedTheme) {
      setTheme(savedTheme);
    } else if (systemPrefersDark) {
      setTheme('night');
    }
  }, []);
  
  // Save theme preference
  useEffect(() => {
    localStorage.setItem('portfolio-theme', theme);
  }, [theme]);
  
  // Typewriter effect
  useEffect(() => {
    const handleType = () => {
      const i = loopNum % texts.length;
      const fullText = texts[i];
      
      setTypedText(isDeleting 
        ? fullText.substring(0, typedText.length - 1)
        : fullText.substring(0, typedText.length + 1)
      );
      
      setTypingSpeed(isDeleting ? 50 : 150);
      
      if (!isDeleting && typedText === fullText) {
        setTimeout(() => setIsDeleting(true), 2000);
      } else if (isDeleting && typedText === '') {
        setIsDeleting(false);
        setLoopNum(loopNum + 1);
      }
    };
    
    const timer = setTimeout(handleType, typingSpeed);
    return () => clearTimeout(timer);
  }, [typedText, isDeleting, loopNum, texts]);
  
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 2500);
    return () => clearTimeout(timer);
  }, []);
  
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);
  
  // Scroll progress and section detection
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight - windowHeight;
      const progress = (scrollY / documentHeight) * 100;
      setScrollProgress(progress);
      
      const sections = ['home', 'work', 'about', 'experience', 'contact'];
      const scrollPosition = window.scrollY + 200;
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Touch gestures for mobile project navigation
  useEffect(() => {
    const handleTouchStart = (e) => {
      touchStartX.current = e.touches[0].clientX;
    };
    
    const handleTouchEnd = (e) => {
      const touchEndX = e.changedTouches[0].clientX;
      const diff = touchStartX.current - touchEndX;
      
      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          nextProject();
        } else {
          prevProject();
        }
      }
    };
    
    const container = containerRef.current;
    if (container) {
      container.addEventListener('touchstart', handleTouchStart);
      container.addEventListener('touchend', handleTouchEnd);
    }
    
    return () => {
      if (container) {
        container.removeEventListener('touchstart', handleTouchStart);
        container.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, []);
  
  // Toggle theme
  const toggleTheme = () => {
    setTheme(theme === 'night' ? 'bubblegum' : 'night');
  };

const projects = [
  {
    id: 1,
    title: "Intelligent Traffic Monitoring System",
    subtitle: "Computer Vision & Deep Learning",
    description: "Engineered end-to-end computer vision pipeline for traffic law enforcement with 94% helmet detection and 87% license plate recognition accuracy using state-of-the-art deep learning models. Implemented real-time video processing with multi-threading optimization achieving 30 FPS on standard hardware with GPU acceleration.",
    tech: ["Python", "YOLOv8", "OpenCV", "TensorFlow", "Computer Vision", "Multi-threading"],
    year: "2025",
    githubUrl: "https://github.com/Deeptiwakchaure",
    imageUrl: "traffic-monitoring.png",
    category: "ai",
    highlights: ["94% accuracy", "Real-time processing", "30 FPS performance"]
  },
  {
    id: 2,
    title: "Interactive Gaming Application on Hardware",
    subtitle: "FPGA Development & Digital Design",
    description: "Implemented real-time Snake game using Verilog HDL on Xilinx FPGA with VGA controller (640×480@60Hz) and advanced collision detection algorithms. Designed modular hardware architecture with clock dividers, LFSR random generation, and finite state machine achieving <16ms input latency for seamless gameplay.",
    tech: ["Verilog", "VHDL", "VGA Controller", "Xilinx FPGA", "Digital Design", "FSM"],
    year: "2025",
    githubUrl: "https://github.com/Deeptiwakchaure",
    imageUrl: "gaming-hardware.png",
    category: "hardware",
    highlights: ["<16ms latency", "VGA 640x480@60Hz", "Modular architecture"]
  },
  {
    id: 3,
    title: "Dice Probability Game",
    subtitle: "Web Development & Probability Simulation",
    description: "Developed interactive web-based dice probability calculator with realistic rolling animations and visual feedback. Implemented dynamic probability calculations using NumPy, responsive UI with Bootstrap, and interactive dice animations using JavaScript and CSS. The application allows users to calculate probabilities of achieving specific target sums with multiple dice and visualize the results in real-time.",
    tech: ["Python", "Flask", "HTML", "CSS", "JavaScript", "Bootstrap", "NumPy"],
    year: "2025",
    githubUrl: "https://github.com/Deeptiwakchaure",
    imageUrl: "dice-probability.png",
    category: "web",
    highlights: ["Real-time visualization", "Interactive animations", "Probability calculations"]
  },
  {
    id: 4,
    title: "IoT-Based RFID Access Control System",
    subtitle: "IoT & Cloud Integration",
    description: "Developed enterprise-grade secure access control system using ESP8266 and RC522 RFID module with real-time authentication and cloud-based logging via Google Sheets API integration. Implemented comprehensive timestamp tracking, database management, and security audit trails with 99.9% uptime reliability.",
    tech: ["ESP8266", "RFID", "Google Sheets API", "Firebase", "IoT", "Cloud Computing"],
    year: "2024",
    githubUrl: "https://github.com/Deeptiwakchaure",
    imageUrl: "rfid-access.png",
    category: "iot",
    highlights: ["99.9% uptime", "Cloud integration", "Real-time authentication"]
  },
  {
    id: 5,
    title: "Image Color Analyzer",
    subtitle: "Image Processing & Color Analysis",
    description: "Developed a web-based tool for analyzing color distributions in images. Implemented image processing algorithms to extract dominant colors, generate color palettes, and provide detailed color statistics. The application allows users to upload images and receive comprehensive color analysis including RGB, HEX, and HSL values with visual representations.",
    tech: ["Python", "Flask", "OpenCV", "Pillow", "HTML", "CSS", "JavaScript"],
    year: "2024",
    githubUrl: "https://github.com/Deeptiwakchaure",
    imageUrl: "imagecolor.png",
    category: "ai",
    highlights: ["Color extraction", "Palette generation", "Multiple color formats"]
  },
  {
  id: 6,
  title: "WebCam-Controlled ESP32 Car",
  subtitle: "IoT & Remote Control Systems",
  description: "Developed WiFi-enabled remote controlled surveillance car using ESP32-CAM module with real-time video streaming and web-based control interface. Implemented bidirectional communication, motor control via L298N driver, and responsive web server with live camera feed achieving <200ms latency for seamless remote operation.",
  tech: ["ESP32-CAM", "WiFi", "Web Server", "Motor Control", "Video Streaming", "IoT"],
  year: "2024",
  githubUrl: "https://github.com/Deeptiwakchaure",
  imageUrl: "esp32-car.png",
  category: "iot",
  highlights: ["<200ms latency", "Live video streaming", "Web-based control"]
  },  
  {
    id: 7,
    title: "House Price Prediction Model",
    subtitle: "Machine Learning & Data Analysis",
    description: "Developed comprehensive machine learning model for real estate price prediction using advanced regression techniques and feature engineering. Implemented data preprocessing, exploratory data analysis, and model selection achieving 92% accuracy with Random Forest and XGBoost algorithms on California housing dataset.",
    tech: ["Python", "Scikit-learn", "Pandas", "NumPy", "Random Forest", "XGBoost"],
    year: "2024",
    githubUrl: "https://github.com/Deeptiwakchaure",
    imageUrl: "house-prediction.png",
    category: "ai",
    highlights: ["92% accuracy", "Feature engineering", "Multiple algorithms"]
  },
  {
    id: 8,
    title: "Cloud Account Management",
    subtitle: "Full-Stack Development & Cloud Solutions",
    description: "Developed a comprehensive cloud-based accounting platform with GST compliance and financial reporting features. Implemented secure user authentication, real-time data synchronization, and multi-tenant architecture for scalable business operations. The solution includes automated invoice generation, expense tracking, and financial analytics dashboard.",
    tech: ["Python", "Flask", "React", "MongoDB", "GST API", "Cloud Computing", "JWT Authentication"],
    year: "2025",
    githubUrl: "https://github.com/Deeptiwakchaure",
    imageUrl: "account-management.png",
    category: "web",
    highlights: ["GST compliance", "Real-time sync", "Multi-tenant architecture"]
  },
  {
    id: 9,
    title: "Dynamic Form Builder",
    subtitle: "Web Development & UI Components",
    description: "Created a versatile form builder application enabling users to create, customize, and deploy forms with various input types. Implemented drag-and-drop functionality, real-time preview, and data collection with validation. Features include conditional logic, form templates, and integration with external APIs for data persistence.",
    tech: ["React", "Node.js", "Express", "MongoDB", "HTML/CSS", "JavaScript", "Drag-and-Drop API"],
    year: "2025",
    githubUrl: "https://github.com/Deeptiwakchaure",
    imageUrl: "form-builder.png",
    category: "web",
    highlights: ["Drag-and-drop", "Real-time preview", "Conditional logic"]
  },
  {
    id: 10,
    title: "AI Blog Generator",
    subtitle: "AI/ML & Natural Language Processing",
    description: "Developed an AI-powered content generation tool using state-of-the-art language models. Implemented user-friendly interface for generating blog posts on various topics with customizable tone and length. Features include topic suggestion, content optimization, and multi-language support with integration to popular blogging platforms.",
    tech: ["Python", "Streamlit", "Hugging Face", "NLP", "OpenAI API", "Flask", "Transformers"],
    year: "2025",
    githubUrl: "https://github.com/Deeptiwakchaure",
    imageUrl: "ai-blog-generator.png",
    category: "ai",
    highlights: ["Multiple models", "Customizable output", "Multi-language support"]
  },
  // UI/UX Projects with detailed case study data
  {
    id: 11,
    title: "EduReader - E-Textbook Platform",
    subtitle: "Figma, User Research, Design Systems",
    description: "Architected comprehensive UI/UX with 40+ screens and developed wireframes with interaction notes for improved usability. Led iterative user research and testing driving accessibility improvements based on direct student feedback.",
    tech: ["Figma", "User Research", "Design Systems", "Wireframing", "Accessibility", "User Testing"],
    year: "2025",
    caseStudyUrl: "/case-study/edu-reader",
    imageUrl: "edu-reader-hero1.png",
    category: "uiux",
    highlights: ["40+ screens", "Accessibility improvements", "Student-centered design"],
    caseStudy: {
      heroImage: "edu-reader-hero.png",
      overview: "EduReader is a comprehensive e-textbook platform designed to revolutionize the digital learning experience for students and educators. The project involved creating an intuitive interface that makes digital textbooks more accessible, interactive, and engaging.",
      challenge: "The main challenge was to design a platform that could handle complex educational content while maintaining simplicity and ease of use. We needed to ensure accessibility for all students, including those with disabilities, while providing advanced features for power users.",
      process: "The design process began with extensive user research, including interviews with students, teachers, and accessibility experts. We created detailed user personas and journey maps to understand the diverse needs of our users. Multiple iterations of wireframes and prototypes were tested with real users to refine the experience.",
      processImages: [
        "mood1.png",
        "mood2.png",
        "mood3.png"
      ],
      features: [
        {
          title: "Adaptive Reading Interface",
          description: "Customizable reading experience with adjustable font sizes, spacing, and color schemes to accommodate different learning preferences and accessibility needs.",
          image: "edu-reader-feature-1.png"
        },
        {
          title: "Interactive Annotations",
          description: "Advanced annotation system allowing students to highlight, take notes, and collaborate directly within the textbook content.",
          image: "edu-reader-feature-2.png"
        },
        {
          title: "Progress Tracking",
          description: "Comprehensive dashboard for students and teachers to monitor reading progress, comprehension, and engagement metrics.",
          image: "edu-reader-feature-3.png"
        }
      ],
      UserFlow: "The platform was successfully launched with a 95% user satisfaction rate. Accessibility improvements led to a 40% increase in engagement among students with disabilities. The system now supports over 50,000 active users across multiple educational institutions.",
      resultsImages: [
        "SwimLane.png",
        "SwimLane2.png"
      ],
      lessons: "This project emphasized the importance of inclusive design and the need for continuous user feedback throughout the development process. We learned that accessibility features benefit all users, not just those with specific needs."
    }
  },
  {
    id: 12,
    title: "TrailIQ - Team Collaboration Platform",
    subtitle: "Figma, Workflow Design, Information Architecture",
    description: "Designed end-to-end project management UX reducing task completion steps from 5 to 3 through intuitive workflows. Developed responsive cross-platform layouts with optimized information architecture minimizing cognitive load.",
    tech: ["Figma", "Workflow Design", "Information Architecture", "Cross-platform Design", "UX Optimization"],
    year: "2025",
    caseStudyUrl: "/case-study/trail-iq",
    imageUrl: "trail-iq-hero-1.png",
    category: "uiux",
    highlights: ["Reduced task steps", "Optimized information architecture", "Cross-platform design"],
    caseStudy: {
      heroImage: "trail-iq-hero.png",
      overview: "TrailIQ is a modern team collaboration platform designed to streamline project management and enhance team productivity. The focus was on creating an intuitive interface that reduces complexity while maintaining powerful functionality.",
      challenge: "The primary challenge was to simplify complex project management workflows without sacrificing functionality. We needed to design an information architecture that could handle multiple project types and team structures while keeping the interface clean and approachable.",
      process: "We conducted extensive research with project managers, team leads, and team members across various industries. This research informed our information architecture and workflow design. We created multiple design iterations, testing each with real teams to validate our assumptions and refine the user experience.",
      processImages: [
        "Moodboard.png",
        "Moodboard2.png",
        "Moodboard3.png"
      ],
      features: [
        {
          title: "Smart Dashboard",
          description: "Intelligent dashboard that prioritizes relevant information based on user role and current context, reducing information overload.",
          image: "trail-iq-process-1.png"
        },
        {
          title: "Streamlined Task Management",
          description: "Reduced task completion from 5 steps to 3 through intelligent automation and contextual actions.",
          image: "trail-iq-process-2.png"
        },
        {
          title: "Unified Communication",
          description: "Integrated chat, video calls, and file sharing within the project context to minimize context switching.",
          image: "trail-iq-process-3.png"
        }
      ],
      UserFlow: "The redesigned platform resulted in a 60% reduction in time-on-task for common activities. User satisfaction increased by 45%, and team productivity metrics showed a 35% improvement. The platform now serves over 100,000 teams worldwide.",
      resultsImages: [
        "SwimLane.png",
        "SwimLane.png"
      ],
      lessons: "We learned that simplification doesn't mean removing features—it means presenting them in the right context. The importance of understanding user mental models and designing workflows that align with natural thought processes became clear through this project."
    }
  }
];
  
  const skills = [
    { category: "Programming", items: ["Python", "Java", "JavaScript", "C++", "SQL"], level: 95 },
    { category: "AI/ML", items: ["TensorFlow", "PyTorch", "OpenCV", "YOLOv8", "Computer Vision"], level: 90 },
    { category: "Web Technologies", items: ["React", "Node.js", "HTML/CSS", "Figma", "RESTful APIs"], level: 85 },
    { category: "Tools & Platforms", items: ["Git", "Docker", "AWS", "Linux", "PostgreSQL"], level: 80 },
    { category: "Frameworks", items: ["Django", "Flask", "Express.js", "Matplotlib"], level: 85 }
  ];
  
  const experiences = [
    {
      id: 1,
      title: "AI & ML Development Intern",
      company: "Prodigy InfoTech",
      period: "2025",
      location: "Virtual",
      description: "Developed advanced AI solutions focusing on computer vision and machine learning applications. Implemented real-time processing systems and optimized model performance for production environments.",
      achievements: [
        "Optimized deep learning models for real-time inference",
        "Developed computer vision pipeline with 94% accuracy",
        "Implemented scalable data processing architecture"
      ],
      logo: "prodigy.jpg"
    },
    {
      id: 2,
      title: "Robotics Development Intern",
      company: "AICTE IdeaLab, SPIT",
      period: "2025",
      location: "Mumbai, India",
      description: "Designed and implemented autonomous robotic systems with sensor integration and path planning algorithms. Developed embedded control systems for multi-robot coordination and real-time navigation solutions.",
      achievements: [
        "Designed autonomous navigation systems for mobile robots",
        "Implemented sensor fusion algorithms for environment perception",
        "Developed control systems with <20ms response time"
      ],
      logo: "aicte.png"
    },
    {
      id: 3,
      title: "EXTC Engineering Student",
      company: "Rajiv Gandhi Institute of Technology",
      period: "2022 - 2026",
      location: "Mumbai, India",
      description: "Specializing in Electronics and Telecommunication Engineering with focus on AI/ML, embedded systems, and web interfaces. Maintaining excellence in academic performance while pursuing advanced research projects.",
      achievements: [
        "CGPA: 7.03/10",
        "Published a technical paper in magazine and working on a research papers on computer vision",
        "Led multiple technical projects and competitions"
      ],
      logo: "rgit.jpeg"
    }
  ];
  
  const projectCategories = [
    { id: 'all', name: 'All Projects', icon: <Code className="w-4 h-4" /> },
    { id: 'ai', name: 'AI/ML', icon: <Cpu className="w-4 h-4" /> },
    { id: 'web', name: 'Web Dev', icon: <Globe className="w-4 h-4" /> },
    { id: 'iot', name: 'IoT', icon: <Zap className="w-4 h-4" /> },
    { id: 'hardware', name: 'Hardware', icon: <HardDrive className="w-4 h-4" /> },
    { id: 'uiux', name: 'UI/UX', icon: <Palette className="w-4 h-4" /> }
  ];
  
  const filteredProjects = projectFilter === 'all' 
    ? projects 
    : projects.filter(project => project.category === projectFilter);
  
  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
  };
  
  const nextProject = () => {
    setCurrentProjectIndex((prev) => (prev + 1) % filteredProjects.length);
  };
  
  const prevProject = () => {
    setCurrentProjectIndex((prev) => (prev - 1 + filteredProjects.length) % filteredProjects.length);
  };

  const openCaseStudy = (projectIndex) => {
    const project = filteredProjects[projectIndex];
    if (project.category === 'uiux') {
      const uxProjects = projects.filter(p => p.category === 'uiux');
      const uxIndex = uxProjects.findIndex(p => p.id === project.id);
      setCurrentCaseStudyIndex(uxIndex);
      setShowCaseStudy(true);
    }
  };

  const closeCaseStudy = () => {
    setShowCaseStudy(false);
  };

  const nextCaseStudy = () => {
    const uxProjects = projects.filter(p => p.category === 'uiux');
    setCurrentCaseStudyIndex((prev) => (prev + 1) % uxProjects.length);
  };

  const previousCaseStudy = () => {
    const uxProjects = projects.filter(p => p.category === 'uiux');
    setCurrentCaseStudyIndex((prev) => (prev - 1 + uxProjects.length) % uxProjects.length);
  };

  const getCurrentCaseStudy = () => {
    const uxProjects = projects.filter(p => p.category === 'uiux');
    return uxProjects[currentCaseStudyIndex];
  };

  const hasNextCaseStudy = () => {
    const uxProjects = projects.filter(p => p.category === 'uiux');
    return currentCaseStudyIndex < uxProjects.length - 1;
  };

  const hasPreviousCaseStudy = () => {
    return currentCaseStudyIndex > 0;
  };
  
  if (isLoading) {
    return (
      <div className={`min-h-screen ${theme === 'bubblegum' ? 'bg-pink-200' : 'bg-black'} flex flex-col items-center justify-center transition-colors duration-1000`}>
        <div className="text-center">
          <div className={`text-4xl md:text-6xl font-light ${theme === 'bubblegum' ? 'text-pink-600' : 'text-white'} mb-12`}>
            Loading
          </div>
          <div className="flex space-x-2 justify-center">
            {[1, 2, 3].map((dot) => (
              <div 
                key={dot}
                className={`w-4 h-4 rounded-full ${theme === 'bubblegum' ? 'bg-pink-600' : 'bg-white'} animate-bounce`}
                style={{ animationDelay: `${dot * 0.2}s` }}
              ></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Show Case Study if active
  if (showCaseStudy) {
    return (
      <CaseStudy 
        project={getCurrentCaseStudy()}
        onBack={closeCaseStudy}
        onNext={nextCaseStudy}
        onPrevious={previousCaseStudy}
        hasNext={hasNextCaseStudy()}
        hasPrevious={hasPreviousCaseStudy()}
        theme={theme}
      />
    );
  }
  
  return (
    <div className={`min-h-screen ${theme === 'bubblegum' ? 'bg-pink-200' : 'bg-black'} text-white font-light overflow-x-hidden transition-colors duration-1000 relative`}>
      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        #projects-dock {
          scroll-behavior: smooth;
          scroll-snap-type: x mandatory;
        }
        #projects-dock > div {
          scroll-snap-align: center;
        }
        .glow-effect {
          box-shadow: 0 0 20px rgba(255, 105, 180, 0.1);
        }
        .glow-effect:hover {
          box-shadow: 0 0 30px rgba(255, 105, 180, 0.3);
        }
        .project-card {
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          transform-style: preserve-3d;
          will-change: transform;
        }
        .project-card:hover {
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.7), 0 0 20px rgba(255, 105, 180, 0.3);
        }
        .nav-link {
          position: relative;
          overflow: hidden;
        }
        .nav-link::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 0;
          height: 1px;
          background: #ff69b4;
          transition: width 0.3s ease;
        }
        .nav-link:hover::after {
          width: 100%;
        }
        .skill-tag {
          background: linear-gradient(135deg, rgba(255, 105, 180, 0.1), rgba(255, 105, 180, 0.05));
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
          will-change: transform, box-shadow;
        }
        .skill-tag:hover {
          background: linear-gradient(135deg, rgba(255, 105, 180, 0.2), rgba(255, 105, 180, 0.1));
          transform: translateY(-2px);
          box-shadow: 0 0 15px rgba(255, 105, 180, 0.3);
        }
        .contact-card {
          background: linear-gradient(135deg, rgba(255, 105, 180, 0.05), rgba(255, 105, 180, 0.02));
          backdrop-filter: blur(10px);
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          will-change: transform, box-shadow;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
        }
        .contact-card:hover {
          background: linear-gradient(135deg, rgba(255, 105, 180, 0.15), rgba(255, 105, 180, 0.08));
          transform: translateY(-5px);
          box-shadow: 0 10px 25px rgba(255, 105, 180, 0.2);
        }
        .hero-text {
          text-shadow: 0 0 30px rgba(255, 105, 180, 0.5);
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out forwards;
          opacity: 0;
          transform: translateY(20px);
          will-change: opacity, transform;
        }
        .delay-200 { animation-delay: 200ms; }
        .delay-400 { animation-delay: 400ms; }
        .delay-600 { animation-delay: 600ms; }
        .delay-800 { animation-delay: 800ms; }
        
        @keyframes fadeInUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .cursor-trail {
          position: fixed;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          pointer-events: none;
          z-index: 9999;
          mix-blend-mode: difference;
          ${theme === 'bubblegum' ? 'background: #ff69b4;' : 'background: #ff69b4;'}
          transition: transform 0.1s ease-out;
          will-change: transform;
        }
        
        .border-hover:hover {
          border-color: #ff69b4;
        }
        
        .text-hover:hover {
          color: #ff69b4;
        }
        
        .pink-glow:hover {
          box-shadow: 0 0 20px rgba(255, 105, 180, 0.4);
        }
        
        .hover-scale:hover {
          transform: scale(1.05);
        }
        
        .hover-brightness:hover {
          filter: brightness(1.2);
          drop-shadow: 0 0 20px rgba(255, 105, 180, 0.3);
        }
        
        /* Bubblegum theme overrides */
        .bubblegum-theme {
          --text-primary: #ff69b4;
          --text-secondary: #d65db1;
          --bg-primary: #fff1f6;
          --bg-secondary: #ffccdd;
          --accent: #ff69b4;
        }
        
        /* Night theme overrides */
        .night-theme {
          --text-primary: #ffffff;
          --text-secondary: #cccccc;
          --bg-primary: #000000;
          --bg-secondary: #111111;
          --accent: #ff69b4;
        }
        
        /* Animation for theme transition */
        .theme-transition {
          transition: background-color 0.8s ease, color 0.8s ease, border-color 0.8s ease;
        }
        
        /* Loading animations */
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-15px); }
        }
        
        .float-animation {
          animation: float 3s ease-in-out infinite;
          will-change: transform;
        }
        
        /* Scroll down animation */
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        
        .bounce-animation {
          animation: bounce 2s ease-in-out infinite;
          will-change: transform;
        }
        
        /* Project card hover animation */
        .project-card-hover {
          transition: all 0.3s ease;
          will-change: transform;
        }
        
        .project-card-hover:hover {
          transform: translateY(-5px);
        }
        
        /* Skill tag animation */
        .skill-tag-animation {
          transition: all 0.3s ease;
          will-change: transform;
        }
        
        .skill-tag-animation:hover {
          transform: scale(1.1) rotate(2deg);
        }
        
        /* Mobile menu animation */
        .mobile-menu {
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          will-change: transform;
        }
        
        .mobile-menu-open {
          transform: translateX(0);
        }
        
        .mobile-menu-closed {
          transform: translateX(100%);
        }
        
        /* Bouncing dots animation */
        @keyframes bounce {
          0%, 80%, 100% {
            transform: scale(0);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }
        
        /* Enhanced glow effects */
        .glow-text {
          text-shadow: 0 0 10px rgba(255, 105, 180, 0.5);
        }
        
        .glow-box {
          box-shadow: 0 0 15px rgba(255, 105, 180, 0.2);
        }
        
        .glow-button {
          box-shadow: 0 0 15px rgba(255, 105, 180, 0.3);
        }
        
        .glow-button:hover {
          box-shadow: 0 0 25px rgba(255, 105, 180, 0.5);
        }
        
        /* Mobile menu item animation */
        .menu-item {
          position: relative;
          overflow: hidden;
        }
        
        .menu-item::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 105, 180, 0.2), transparent);
          transition: left 0.5s;
        }
        
        .menu-item:hover::before {
          left: 100%;
        }
        
        /* Responsive improvements */
        @media (max-width: 768px) {
          .hero-section {
            padding-top: 80px;
          }
          
          .project-card {
            min-width: 280px;
          }
          
          .mobile-menu-item {
            padding: 16px 0;
            font-size: 18px;
          }
        }
        
        /* Enhanced shadow effects */
        .soft-shadow {
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
        }
        
        .medium-shadow {
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
        }
        
        .strong-shadow {
          box-shadow: 0 15px 35px rgba(0, 0, 0, 0.4);
        }
        
        /* Scroll progress bar */
        .scroll-progress {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 3px;
          background: rgba(255, 255, 255, 0.1);
          z-index: 1000;
        }
        
        .scroll-progress-bar {
          height: 100%;
          background: linear-gradient(90deg, #ff69b4, #ff1493);
          transition: width 0.3s ease;
        }
        
        /* 3D tilt effect */
        .tilt-card {
          transform-style: preserve-3d;
          transition: transform 0.6s;
          will-change: transform;
        }
        
        /* Typewriter cursor */
        .typewriter-cursor {
          display: inline-block;
          width: 3px;
          height: 1.2em;
          background-color: currentColor;
          animation: blink 1s infinite;
        }
        
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        
        /* Filter button styles */
        .filter-button {
          transition: all 0.3s ease;
          will-change: background-color, color;
        }
        
        .filter-button.active {
          background: #ff69b4;
          color: white;
        }
        
        /* Micro-interactions */
        .micro-interaction {
          transition: all 0.2s ease;
          will-change: transform;
        }
        
        .micro-interaction:active {
          transform: scale(0.95);
        }
        
        /* Skill cloud styles - Desktop */
        .skill-cloud {
          position: relative;
          width: 100%;
          height: 400px;
          perspective: 1000px;
        }
        
        .skill-sphere {
          position: absolute;
          width: 100%;
          height: 100%;
          transform-style: preserve-3d;
          animation: rotate 20s infinite linear;
          will-change: transform;
        }
        
        @keyframes rotate {
          from { transform: rotateY(0deg); }
          to { transform: rotateY(360deg); }
        }
        
        .skill-item {
          position: absolute;
          padding: 10px 20px;
          background: ${theme === 'bubblegum' ? 'rgba(255, 105, 180, 0.2)' : 'rgba(255, 105, 180, 0.1)'};
          border: 1px solid ${theme === 'bubblegum' ? '#ff69b4' : '#ff69b4'};
          border-radius: 20px;
          cursor: pointer;
          transition: all 0.3s ease;
          will-change: transform, background-color;
        }
        
        .skill-item:hover {
          transform: scale(1.2);
          background: ${theme === 'bubblegum' ? 'rgba(255, 105, 180, 0.4)' : 'rgba(255, 105, 180, 0.3)'};
        }
        
        /* Mobile skills grid */
        .skills-grid {
          display: none;
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
          gap: 15px;
        }
        
        .mobile-skill-item {
          padding: 12px 16px;
          background: ${theme === 'bubblegum' ? 'rgba(255, 105, 180, 0.2)' : 'rgba(255, 105, 180, 0.1)'};
          border: 1px solid ${theme === 'bubblegum' ? '#ff69b4' : '#ff69b4'};
          border-radius: 16px;
          text-align: center;
          transition: all 0.3s ease;
        }
        
        .mobile-skill-item:hover {
          background: ${theme === 'bubblegum' ? 'rgba(255, 105, 180, 0.4)' : 'rgba(255, 105, 180, 0.3)'};
          transform: translateY(-3px);
        }
        
        .mobile-skill-category {
          grid-column: 1 / -1;
          margin-top: 20px;
          margin-bottom: 10px;
          font-size: 18px;
          font-weight: 500;
          color: ${theme === 'bubblegum' ? '#ff69b4' : '#ff69b4'};
        }
        
        /* 3D timeline styles */
        .timeline-3d {
          position: relative;
          padding: 50px 0;
          perspective: 1000px;
        }
        
        .timeline-item {
          position: relative;
          padding: 20px;
          background: ${theme === 'bubblegum' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)'};
          border-radius: 10px;
          margin-bottom: 50px;
          transform-style: preserve-3d;
          transition: all 0.5s ease;
          will-change: transform;
        }
        
        .timeline-item:hover {
          transform: translateZ(50px);
        }
        
        /* Experience card styles */
        .experience-card {
          position: relative;
          background: ${theme === 'bubblegum' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.05)'};
          border-radius: 15px;
          padding: 25px;
          margin-bottom: 30px;
          transition: all 0.4s ease;
          will-change: transform, box-shadow;
        }
        
        .experience-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
        }
        
        .experience-header {
          display: flex;
          align-items: center;
          margin-bottom: 15px;
        }
        
        .experience-logo {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          object-fit: cover;
          margin-right: 15px;
          border: 2px solid ${theme === 'bubblegum' ? '#ff69b4' : '#ff69b4'};
        }
        
        .experience-title {
          flex: 1;
        }
        
        .experience-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 15px;
          margin-bottom: 15px;
        }
        
        .experience-meta-item {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 14px;
          color: ${theme === 'bubblegum' ? '#ff69b4' : '#ff69b4'};
        }
        
        .experience-description {
          margin-bottom: 15px;
          line-height: 1.6;
        }
        
        .experience-achievements {
          margin-top: 15px;
        }
        
        .experience-achievements h4 {
          font-size: 16px;
          margin-bottom: 10px;
          color: ${theme === 'bubblegum' ? '#ff69b4' : '#ff69b4'};
        }
        
        .experience-achievements ul {
          list-style-type: none;
          padding-left: 0;
        }
        
        .experience-achievements li {
          position: relative;
          padding-left: 25px;
          margin-bottom: 8px;
          line-height: 1.5;
        }
        
        .experience-achievements li:before {
          content: "✓";
          position: absolute;
          left: 0;
          color: ${theme === 'bubblegum' ? '#ff69b4' : '#ff69b4'};
        }
        
        /* Timeline connector */
        .timeline-connector {
          position: absolute;
          top: 0;
          left: 30px;
          bottom: 0;
          width: 2px;
          background: ${theme === 'bubblegum' ? '#ff69b4' : '#ff69b4'};
          z-index: -1;
        }
        
        .timeline-dot {
          position: absolute;
          left: 20px;
          top: 30px;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: ${theme === 'bubblegum' ? '#ff69b4' : '#ff69b4'};
          border: 4px solid ${theme === 'bubblegum' ? '#fff1f6' : '#000'};
          z-index: 1;
        }
        
        /* Media queries for responsive design */
        @media (max-width: 768px) {
          .skill-cloud {
            display: none;
          }
          
          .skills-grid {
            display: grid;
          }
          
          .experience-card {
            padding: 20px;
            margin-left: 15px;
          }
          
          .experience-header {
            flex-direction: column;
            align-items: flex-start;
          }
          
          .experience-logo {
            margin-right: 0;
            margin-bottom: 10px;
          }
          
          .timeline-connector {
            left: 15px;
          }
          
          .timeline-dot {
            left: 5px;
          }
        }
        
        /* Mobile menu improvements */
        @media (max-width: 640px) {
          .mobile-menu {
            width: 100%;
          }
          
          .nav-link {
            font-size: 14px;
          }
          
          .hero-section {
            padding-top: 100px;
          }
          
          h1 {
            font-size: 2.5rem !important;
          }
          
          .project-card {
            min-width: 240px;
          }
          
          .contact-card {
            padding: 15px;
          }
        }
        
        /* Dribbble and Behance specific styles */
        .dribbble-icon {
          color: #ea4c89;
        }
        
        .behance-icon {
          color: #1769ff;
        }
        
        .dribbble-card:hover {
          background: linear-gradient(135deg, rgba(234, 76, 137, 0.15), rgba(234, 76, 137, 0.05));
          border-color: #ea4c89;
        }
        
        .behance-card:hover {
          background: linear-gradient(135deg, rgba(23, 105, 255, 0.15), rgba(23, 105, 255, 0.05));
          border-color: #1769ff;
        }
        
        .dribbble-card:hover .dribbble-icon {
          color: #fff;
        }
        
        .behance-card:hover .behance-icon {
          color: #fff;
        }
        
        /* Contact section improvements */
        .contact-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 2rem;
          max-width: 1200px;
          margin: 0 auto;
        }
        
        @media (max-width: 768px) {
          .contact-grid {
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 1.5rem;
          }
        }
        
        @media (max-width: 480px) {
          .contact-grid {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
        }
      `}</style>
      
      {/* Scroll Progress Bar */}
      <div className="scroll-progress">
        <div 
          className="scroll-progress-bar" 
          style={{ width: `${scrollProgress}%` }}
        ></div>
      </div>
      
      {/* Custom Cursor */}
      <div 
        className="cursor-trail"
        style={{
          left: mousePosition.x - 10,
          top: mousePosition.y - 10,
          transform: 'translate3d(0, 0, 0)',
          boxShadow: '0 0 10px rgba(255, 105, 180, 0.5)'
        }}
      />
      
      {/* Mobile Menu */}
      <div className={`fixed top-0 right-0 h-full w-80 z-50 ${theme === 'bubblegum' ? 'bg-pink-200' : 'bg-black/95'} backdrop-blur-lg border-l border-white/20 mobile-menu ${isMenuOpen ? 'mobile-menu-open' : 'mobile-menu-closed'} medium-shadow`}>
        <div className="p-8">
          <button 
            onClick={() => setIsMenuOpen(false)}
            className={`absolute top-6 right-6 p-2 rounded-full ${theme === 'bubblegum' ? 'bg-pink-600' : 'bg-gray-800'} hover:scale-110 transition-transform duration-300 glow-button micro-interaction`}
            aria-label="Close menu"
          >
            <X className="h-5 w-5 text-white" />
          </button>
          
          <div className="mt-16 space-y-2">
            {['HOME', 'WORK', 'ABOUT', 'EXPERIENCE', 'CONTACT'].map((section, index) => (
              <button
                key={section}
                onClick={() => scrollToSection(section.toLowerCase())}
                className={`menu-item mobile-menu-item block text-left w-full py-4 px-2 rounded-lg transition-all duration-300 ${
                  activeSection === section.toLowerCase() 
                    ? `${theme === 'bubblegum' ? 'bg-pink-600 text-white' : 'bg-gray-800 text-white'} font-medium glow-box` 
                    : `${theme === 'bubblegum' ? 'text-pink-700 hover:bg-pink-300/30' : 'text-gray-400 hover:bg-gray-800'}`
                }`}
              >
                <div className="flex items-center">
                  <span className={`text-sm mr-3 ${theme === 'bubblegum' ? 'text-pink-500' : 'text-gray-500'}`}>
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span>{section}</span>
                </div>
              </button>
            ))}
          </div>
          
          <div className="mt-16 pt-8 border-t border-white/10">
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href="https://github.com/Deeptiwakchaure" 
                target="_blank" 
                rel="noopener noreferrer"
                className={`p-3 rounded-full ${theme === 'bubblegum' ? 'bg-pink-600' : 'bg-gray-800'} hover:scale-110 transition-transform duration-300 glow-button micro-interaction`}
              >
                <Github className="w-5 h-5 text-white" />
              </a>
              <a 
                href="https://linkedin.com/in/deepti-wakchaure-657498230" 
                target="_blank" 
                rel="noopener noreferrer"
                className={`p-3 rounded-full ${theme === 'bubblegum' ? 'bg-pink-600' : 'bg-gray-800'} hover:scale-110 transition-transform duration-300 glow-button micro-interaction`}
              >
                <Linkedin className="w-5 h-5 text-white" />
              </a>
              <a 
                href="https://dribbble.com/deeptiwakchaure" 
                target="_blank" 
                rel="noopener noreferrer"
                className={`p-3 rounded-full ${theme === 'bubblegum' ? 'bg-pink-600' : 'bg-gray-800'} hover:scale-110 transition-transform duration-300 glow-button micro-interaction`}
              >
                <Dribbble className="w-5 h-5 text-white dribbble-icon" />
              </a>
              <a 
                href="https://www.behance.net/deeptiwakchaure" 
                target="_blank" 
                rel="noopener noreferrer"
                className={`p-3 rounded-full ${theme === 'bubblegum' ? 'bg-pink-600' : 'bg-gray-800'} hover:scale-110 transition-transform duration-300 glow-button micro-interaction`}
              >
                <ExternalLink className="w-5 h-5 text-white behance-icon" />
              </a>
              <a 
                href="mailto:deeptiwakchaure@gmail.com" 
                className={`p-3 rounded-full ${theme === 'bubblegum' ? 'bg-pink-600' : 'bg-gray-800'} hover:scale-110 transition-transform duration-300 glow-button micro-interaction`}
              >
                <Mail className="w-5 h-5 text-white" />
              </a>
            </div>
          </div>
        </div>
      </div>
      
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-40 ${theme === 'bubblegum' ? 'bg-pink-200' : 'bg-black/90'} backdrop-blur-md border-b border-white/20 theme-transition soft-shadow`}>
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className={`text-lg md:text-xl font-light tracking-wide hero-text ${theme === 'bubblegum' ? 'text-pink-700' : 'text-white'}`}>
             🪷 DEEPTI'S PORTFOLIO
            </div>
            <div className="flex items-center space-x-4">
              {/* Theme Toggle */}
              <button 
                onClick={toggleTheme}
                className={`p-2 rounded-full ${theme === 'bubblegum' ? 'bg-pink-600' : 'bg-gray-800'} hover:scale-110 transition-transform duration-300 glow-button micro-interaction`}
                aria-label="Toggle theme"
              >
                {theme === 'bubblegum' ? (
                  <Moon className="w-5 h-5 text-white" />
                ) : (
                  <Sun className="w-5 h-5 text-pink-400" />
                )}
              </button>
              
              {/* Desktop Menu */}
              <div className="hidden md:flex space-x-6 lg:space-x-10">
                {['HOME', 'WORK', 'ABOUT', 'EXPERIENCE', 'CONTACT'].map((section, index) => (
                  <button
                    key={section}
                    onClick={() => scrollToSection(section.toLowerCase())}
                    className={`nav-link text-sm tracking-widest transition-all duration-300 ${
                      activeSection === section.toLowerCase() 
                        ? 'text-white glow-text' 
                        : theme === 'bubblegum' ? 'text-pink-700' : 'text-gray-400'
                    }`}
                  >
                    {String(index + 1).padStart(2, '0')} {section}
                  </button>
                ))}
              </div>
              
              {/* Mobile Menu Button */}
              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`md:hidden p-2 rounded-md ${theme === 'bubblegum' ? 'bg-pink-600' : 'bg-gray-800'} hover:scale-110 transition-transform duration-300 glow-button micro-interaction`}
                aria-label="Toggle menu"
              >
                <div className="w-6 h-6 flex flex-col justify-center space-y-1">
                  <div className={`w-full h-0.5 ${theme === 'bubblegum' ? 'bg-pink-300' : 'bg-white'}`}></div>
                  <div className={`w-full h-0.5 ${theme === 'bubblegum' ? 'bg-pink-300' : 'bg-white'}`}></div>
                  <div className={`w-full h-0.5 ${theme === 'bubblegum' ? 'bg-pink-300' : 'bg-white'}`}></div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </nav>
      
      {/* Home Section */}
      <section
        id="home"
        className={`relative min-h-screen hero-section ${theme === 'bubblegum' ? 'bg-pink-200' : 'bg-black'} overflow-hidden px-6 sm:px-12 lg:px-24 flex items-center z-10`}
      >
        {/* Content wrapper */}
        <div className="relative z-10 w-full max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-12">
          {/* Text and CTAs */}
          <div className="flex-1 space-y-6 text-center md:text-left">
            <h1 className={`text-4xl sm:text-5xl md:text-6xl font-black leading-tight ${theme === 'bubblegum' ? 'text-pink-800' : 'text-white'} animate-fade-in-up glow-text`}>
              Deepti Wakchaure
            </h1>
            <p className={`text-lg sm:text-xl md:text-2xl ${theme === 'bubblegum' ? 'text-pink-700' : 'text-gray-300'} font-light max-w-xl animate-fade-in-up delay-200`}>
              {typedText}
              <span className="typewriter-cursor"></span>
            </p>
            <p className={`${theme === 'bubblegum' ? 'text-pink-600' : 'text-gray-400'} max-w-lg leading-relaxed animate-fade-in-up delay-400`}>
             My work blends creativity with functionality — turning ideas into visually appealing and user-focused designs.
            </p>
            
            {/* CTAs */}
            <div className="mt-4 flex flex-wrap items-center gap-4 justify-center md:justify-start animate-fade-in-up delay-600">
              <a
                href="mailto:deeptiwakchaure@gmail.com"
                className={`px-6 py-3 ${theme === 'bubblegum' ? 'bg-pink-700 text-white' : 'bg-white text-black'} font-semibold rounded-full hover:scale-105 transition-transform duration-300 glow-button micro-interaction`}
              >
                Get in Touch
              </a>
              <a
                href="https://github.com/Deeptiwakchaure"
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-2 ${theme === 'bubblegum' ? 'text-pink-700' : 'text-gray-400'} text-hover transition-all duration-300 p-2 rounded-full hover:bg-white/10 micro-interaction`}
              >
                <Github className="w-5 h-5" />
                <span>GitHub</span>
              </a>
              <a
                href="https://linkedin.com/in/deepti-wakchaure-657498230"
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-2 ${theme === 'bubblegum' ? 'text-pink-700' : 'text-gray-400'} text-hover transition-all duration-300 p-2 rounded-full hover:bg-white/10 micro-interaction`}
              >
                <Linkedin className="w-5 h-5" />
                <span>LinkedIn</span>
              </a>
              <a
                href="https://dribbble.com/deeptiwakchaure"
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-2 ${theme === 'bubblegum' ? 'text-pink-700' : 'text-gray-400'} text-hover transition-all duration-300 p-2 rounded-full hover:bg-white/10 micro-interaction dribbble-icon`}
              >
                <Dribbble className="w-5 h-5" />
                <span>Dribbble</span>
              </a>
              <a
                href="https://www.behance.net/deeptiwakchaure"
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-2 ${theme === 'bubblegum' ? 'text-pink-700' : 'text-gray-400'} text-hover transition-all duration-300 p-2 rounded-full hover:bg-white/10 micro-interaction behance-icon`}
              >
                <ExternalLink className="w-5 h-5" />
                <span>Behance</span>
              </a>
            </div>
          </div>
          
          {/* Sleepy Image Section */}
          <div className="flex-1 hidden md:flex justify-center">
            <img
              src="/sleepy.png"
              alt="Sleepy emoji"
              className={`w-64 h-64 object-contain drop-shadow-2xl float-animation transition duration-300 hover-brightness tilt-card`}
              onError={(e) => {
                e.target.src = `https://via.placeholder.com/256x256/ff69b4/ffffff?text=Sleepy`;
              }}
            />
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2">
          <button
            onClick={() => scrollToSection('work')}
            className={`text-${theme === 'bubblegum' ? 'pink-600' : 'gray-400'} text-hover transition-all duration-300 bounce-animation p-2 rounded-full hover:bg-white/10 micro-interaction`}
          >
            <ChevronDown className="w-6 h-6" />
          </button>
        </div>
      </section>
      
      {/* Work Section */}
      <section id="work" className={`min-h-screen py-20 ${theme === 'bubblegum' ? 'bg-pink-200' : 'bg-black'} theme-transition z-10`}>
        <div className="container mx-auto px-6 md:px-8">
          <div className="mb-20">
            <h2 className={`text-4xl md:text-6xl font-light mb-4 hero-text ${theme === 'bubblegum' ? 'text-pink-800' : 'text-white'} animate-fade-in-up glow-text`}>SELECTED WORK</h2>
            <p className={`${theme === 'bubblegum' ? 'text-pink-700' : 'text-gray-300'} text-lg animate-fade-in-up delay-200`}>Projects that showcase technical excellence and innovation</p>
          </div>
          
          {/* Project Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {projectCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setProjectFilter(category.id)}
                className={`filter-button px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 micro-interaction flex items-center gap-2 ${
                  projectFilter === category.id
                    ? 'active'
                    : `${theme === 'bubblegum' ? 'bg-pink-300 text-pink-800' : 'bg-gray-800 text-gray-300'} hover:bg-pink-500 hover:text-white`
                }`}
              >
                {category.icon}
                <span>{category.name}</span>
              </button>
            ))}
          </div>
          
          {/* Project Navigation */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center space-x-4">
              <button
                onClick={prevProject}
                className={`p-3 rounded-full ${theme === 'bubblegum' ? 'bg-pink-600' : 'bg-gray-800'} hover:scale-110 transition-transform duration-300 glow-button micro-interaction`}
                disabled={filteredProjects.length <= 1}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <span className={`text-sm ${theme === 'bubblegum' ? 'text-pink-700' : 'text-gray-400'}`}>
                {filteredProjects.length > 0 ? currentProjectIndex + 1 : 0} / {filteredProjects.length}
              </span>
              <button
                onClick={nextProject}
                className={`p-3 rounded-full ${theme === 'bubblegum' ? 'bg-pink-600' : 'bg-gray-800'} hover:scale-110 transition-transform duration-300 glow-button micro-interaction`}
                disabled={filteredProjects.length <= 1}
              >
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Horizontal Scrolling Dock */}
          <div className="relative mb-20" ref={containerRef}>
            <div className="flex space-x-6 md:space-x-8 overflow-x-auto scrollbar-hide px-4" id="projects-dock">
              {filteredProjects.map((project, index) => (
                <div
                  key={project.id}
                  className={`flex-none relative cursor-pointer project-card-hover group ${
                    index === currentProjectIndex 
                      ? 'w-80 h-96 scale-110' 
                      : 'w-64 h-80 hover:scale-105'
                  }`}
                  onClick={() => setCurrentProjectIndex(index)}
                >
                  <div className={`w-full h-full ${theme === 'bubblegum' ? 'bg-gradient-to-br from-pink-300 to-pink-200' : 'bg-gradient-to-br from-gray-800 to-gray-900'} rounded-xl overflow-hidden relative border ${theme === 'bubblegum' ? 'border-pink-300' : 'border-gray-700'} project-card-hover transition-all duration-300 glow-box tilt-card`}>
                    {/* Project Image */}
                    <div className="absolute inset-0">
                      <img 
                        src={project.imageUrl} 
                        alt={project.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          console.log('Project image failed to load:', e.target.src);
                          e.target.src = `https://via.placeholder.com/320x400/ff69b4/ffffff?text=${encodeURIComponent(project.title)}`;
                        }}
                      />
                      {/* Darker overlay for better text readability */}
                      <div className="absolute inset-0 bg-black/60"></div>
                    </div>
                    
                    {/* Icon based on project type */}
                    <div className="absolute top-6 left-6 z-10">
                      <div className={`w-12 h-12 ${theme === 'bubblegum' ? 'bg-pink-500/30' : 'bg-white/10'} rounded-full flex items-center justify-center backdrop-blur-sm glow-box`}>
                        {project.category === 'uiux' ? (
                          <Palette className="w-6 h-6" />
                        ) : (
                          <Github className="w-6 h-6" />
                        )}
                      </div>
                    </div>
                    
                    {/* Hover Animation Effect */}
                    <div className="absolute inset-0 bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="text-white text-center p-6">
                        <div className="w-16 h-16 border-2 border-white rounded-full flex items-center justify-center mx-auto mb-4 hover-scale transition-transform duration-300 glow-box">
                          <div className="w-0 h-0 border-l-8 border-l-white border-t-4 border-t-transparent border-b-4 border-b-transparent ml-1"></div>
                        </div>
                        <p className="text-sm opacity-80">Click to view details</p>
                      </div>
                    </div>
                    
                    {/* Project Info */}
                    <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black via-black/80 to-transparent">
                      <h3 className={`text-xl font-light mb-2 ${theme === 'bubblegum' ? 'text-pink-800' : 'text-white'}`}>
                        {project.title}
                      </h3>
                      <p className={`${theme === 'bubblegum' ? 'text-pink-700' : 'text-gray-300'} text-sm mb-3`}>
                        {project.subtitle}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {project.tech.slice(0, 3).map((tech, techIndex) => (
                          <span key={techIndex} className={`text-xs ${theme === 'bubblegum' ? 'text-pink-800 bg-pink-200/50' : 'text-gray-200 bg-white/20'} px-2 py-1 rounded backdrop-blur-sm`}>
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    {/* Year Badge */}
                    <div className={`absolute top-6 right-6 ${theme === 'bubblegum' ? 'bg-pink-600 text-white' : 'bg-white text-black'} px-3 py-1 text-xs font-medium rounded-full glow-box`}>
                      {project.year}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Project Details */}
          {filteredProjects.length > 0 && (
            <div className="max-w-4xl mx-auto">
              <div className={`bg-${theme === 'bubblegum' ? 'pink-300/50' : 'gray-900/60'} rounded-xl p-8 backdrop-blur-sm border ${theme === 'bubblegum' ? 'border-pink-400' : 'border-gray-700'} theme-transition glow-effect medium-shadow`}>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className={`text-3xl md:text-4xl font-light mb-4 ${theme === 'bubblegum' ? 'text-pink-800' : 'text-white'} glow-text`}>
                      {filteredProjects[currentProjectIndex].title}
                    </h3>
                    <p className={`${theme === 'bubblegum' ? 'text-pink-700' : 'text-gray-300'} text-lg mb-6`}>
                      {filteredProjects[currentProjectIndex].subtitle}
                    </p>
                    <p className={`${theme === 'bubblegum' ? 'text-pink-600' : 'text-gray-300'} leading-relaxed mb-6`}>
                      {filteredProjects[currentProjectIndex].description}
                    </p>
                    
                    {/* Project Highlights */}
                    <div className="mb-6">
                      <p className={`text-sm ${theme === 'bubblegum' ? 'text-pink-600 uppercase' : 'text-gray-400 uppercase'} tracking-wide mb-3`}>Key Highlights</p>
                      <div className="flex flex-wrap gap-2">
                        {filteredProjects[currentProjectIndex].highlights.map((highlight, index) => (
                          <span key={index} className={`text-xs ${theme === 'bubblegum' ? 'text-pink-800 bg-pink-200/50' : 'text-gray-200 bg-white/20'} px-3 py-1 rounded-full`}>
                            {highlight}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <p className={`text-sm ${theme === 'bubblegum' ? 'text-pink-600 uppercase' : 'text-gray-400 uppercase'} tracking-wide mb-3`}>Technologies</p>
                      <div className="flex flex-wrap gap-2">
                        {filteredProjects[currentProjectIndex].tech.map((tech, index) => (
                          <span key={index} className={`skill-tag-animation text-xs ${theme === 'bubblegum' ? 'text-pink-800 bg-pink-200/50' : 'text-gray-200 bg-white/20'} border ${theme === 'bubblegum' ? 'border-pink-300' : 'border-gray-600'} px-3 py-1 rounded-full glow-box`}>
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="pt-6">
                      {filteredProjects[currentProjectIndex].category === 'uiux' ? (
                        <button 
                          onClick={() => openCaseStudy(currentProjectIndex)}
                          className={`inline-flex items-center space-x-2 text-sm ${theme === 'bubblegum' ? 'text-pink-800' : 'text-gray-300'} text-hover transition-all duration-300 border ${theme === 'bubblegum' ? 'border-pink-400' : 'border-gray-600'} px-4 py-2 rounded-full hover-scale pink-glow glow-button micro-interaction`}
                        >
                          <Palette className="w-4 h-4" />
                          <span>VIEW CASE STUDY</span>
                        </button>
                      ) : (
                        <a 
                          href={filteredProjects[currentProjectIndex].githubUrl} 
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`inline-flex items-center space-x-2 text-sm ${theme === 'bubblegum' ? 'text-pink-800' : 'text-gray-300'} text-hover transition-all duration-300 border ${theme === 'bubblegum' ? 'border-pink-400' : 'border-gray-600'} px-4 py-2 rounded-full hover-scale pink-glow glow-button micro-interaction`}
                        >
                          <Github className="w-4 h-4" />
                          <span>VIEW ON GITHUB</span>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
      
      {/* About Section */}
      <section id="about" className={`min-h-screen py-20 ${theme === 'bubblegum' ? 'bg-pink-200' : 'bg-black'} theme-transition z-10`}>
        <div className="container mx-auto px-6 md:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-16">
              <div>
                <h2 className={`text-4xl md:text-6xl font-light mb-8 hero-text ${theme === 'bubblegum' ? 'text-pink-800' : 'text-white'} animate-fade-in-up glow-text`}>ABOUT</h2>
                <div className="space-y-6 leading-relaxed animate-fade-in-up delay-200">
                  <p className={theme === 'bubblegum' ? 'text-pink-800' : 'text-gray-300'}>
                    I'm an EXTC Engineering student at Rajiv Gandhi Institute of Technology, 
                    specializing in Ui/Ux and Software Development technologies. My passion lies in 
                    creating intelligent systems that solve real-world problems.
                  </p>
                  <p className={theme === 'bubblegum' ? 'text-pink-800' : 'text-gray-300'}>
                    With expertise spanning embedded systems, machine learning, and high-performance 
                    software development, I architect solutions that bridge the gap between 
                    theoretical concepts and practical applications.
                  </p>
                  <p className={theme === 'bubblegum' ? 'text-pink-800' : 'text-gray-300'}>
                    My work focuses on developing robust, scalable systems that deliver measurable 
                    improvements in performance and accuracy across various domains.
                  </p>
                </div>
              </div>
              <div>
                <h3 className={`text-2xl font-light mb-8 ${theme === 'bubblegum' ? 'text-pink-800' : 'text-white'} animate-fade-in-up delay-400 glow-text`}>SKILLS & EXPERTISE</h3>
                
                {/* Desktop: 3D Skill Cloud */}
                <div className="skill-cloud">
                  <div className="skill-sphere">
                    {skills.flatMap((skillGroup, groupIndex) =>
                      skillGroup.items.map((skill, skillIndex) => {
                        const totalSkills = skills.reduce((acc, group) => acc + group.items.length, 0);
                        const index = skills.slice(0, groupIndex).reduce((acc, group) => acc + group.items.length, 0) + skillIndex;
                        const phi = Math.acos(-1 + (2 * index) / totalSkills);
                        const theta = Math.sqrt(totalSkills * Math.PI) * phi;
                        
                        const x = 200 * Math.cos(theta) * Math.sin(phi);
                        const y = 200 * Math.sin(theta) * Math.sin(phi);
                        const z = 200 * Math.cos(phi);
                        
                        return (
                          <div
                            key={skill}
                            className="skill-item"
                            style={{
                              transform: `translate3d(${x}px, ${y}px, ${z}px)`,
                              left: '50%',
                              top: '50%',
                              marginLeft: '-50px',
                              marginTop: '-20px'
                            }}
                            onMouseEnter={() => setActiveSkill(skill)}
                            onMouseLeave={() => setActiveSkill(null)}
                          >
                            {skill}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
                
                {/* Mobile: Skills Grid */}
                <div className="skills-grid">
                  {skills.map((skillGroup, groupIndex) => (
                    <React.Fragment key={groupIndex}>
                      <div className="mobile-skill-category">{skillGroup.category}</div>
                      {skillGroup.items.map((skill, skillIndex) => (
                        <div
                          key={skill}
                          className="mobile-skill-item"
                          onMouseEnter={() => setActiveSkill(skill)}
                          onMouseLeave={() => setActiveSkill(null)}
                        >
                          {skill}
                        </div>
                      ))}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Experience Section */}
      <section id="experience" className={`min-h-screen py-20 ${theme === 'bubblegum' ? 'bg-pink-200' : 'bg-black'} theme-transition z-10`}>
        <div className="container mx-auto px-6 md:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className={`text-4xl md:text-6xl font-light mb-16 hero-text ${theme === 'bubblegum' ? 'text-pink-800' : 'text-white'} animate-fade-in-up glow-text`}>EXPERIENCE</h2>
            
            {/* Enhanced Experience Cards */}
            <div className="relative">
              <div className="timeline-connector"></div>
              
              {experiences.map((exp, index) => (
                <div key={exp.id} className="relative mb-12">
                  <div className="timeline-dot"></div>
                  <div className={`experience-card ${theme === 'bubblegum' ? 'bg-pink-300/30' : 'bg-gray-800/50'} ml-12`}>
                    <div className="experience-header">
                      <img src={exp.logo} alt={exp.company} className="experience-logo" 
                        onError={(e) => {
                          e.target.src = `https://via.placeholder.com/60x60/ff69b4/ffffff?text=${encodeURIComponent(exp.company)}`;
                        }}
                      />
                      <div className="experience-title">
                        <h3 className={`text-2xl font-light ${theme === 'bubblegum' ? 'text-pink-800' : 'text-white'}`}>{exp.title}</h3>
                        <p className={`${theme === 'bubblegum' ? 'text-pink-700' : 'text-gray-300'}`}>{exp.company}</p>
                      </div>
                    </div>
                    
                    <div className="experience-meta">
                      <div className="experience-meta-item">
                        <Calendar className="w-4 h-4" />
                        <span>{exp.period}</span>
                      </div>
                      <div className="experience-meta-item">
                        <MapPin className="w-4 h-4" />
                        <span>{exp.location}</span>
                      </div>
                    </div>
                    
                    <p className={`experience-description ${theme === 'bubblegum' ? 'text-pink-700' : 'text-gray-300'}`}>
                      {exp.description}
                    </p>
                    
                    <div className="experience-achievements">
                      <h4>Key Achievements:</h4>
                      <ul>
                        {exp.achievements.map((achievement, i) => (
                          <li key={i} className={theme === 'bubblegum' ? 'text-pink-700' : 'text-gray-300'}>
                            {achievement}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      {/* Contact Section */}
      <section id="contact" className={`min-h-screen py-20 ${theme === 'bubblegum' ? 'bg-pink-200' : 'bg-black'} theme-transition z-10`}>
        <div className="container mx-auto px-6 md:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className={`text-4xl md:text-6xl font-light mb-16 hero-text ${theme === 'bubblegum' ? 'text-pink-800' : 'text-white'} animate-fade-in-up glow-text`}>LET'S CONNECT</h2>
            
            <div className="space-y-8 mb-16 animate-fade-in-up delay-200">
              <p className={`text-xl ${theme === 'bubblegum' ? 'text-pink-700' : 'text-gray-300'} leading-relaxed`}>
                When I'm not designing, you'll probably find me exploring design trends, tweaking prototypes in Figma, or brainstorming new app ideas. Always open to creative collaborations and feedback!
              </p>
              <p className={`${theme === 'bubblegum' ? 'text-pink-600' : 'text-gray-400'}`}>
                Whether you're looking for technical expertise, research collaboration, 
                or want to explore cutting-edge solutions, I'm here to help.
              </p>
            </div>
            
            <div className="contact-grid">
              <a 
                href="mailto:deeptiwakchaure@gmail.com"
                className={`contact-card p-6 border ${theme === 'bubblegum' ? 'border-pink-400' : 'border-gray-700'} hover:border-white rounded-xl transition-all duration-300 group animate-fade-in-up delay-400 soft-shadow micro-interaction`}
              >
                <Mail className={`w-8 h-8 mb-4 ${theme === 'bubblegum' ? 'text-pink-700' : 'text-gray-400'} group-hover:scale-110 transition-transform duration-300`} />
                <p className={`text-sm ${theme === 'bubblegum' ? 'text-pink-700' : 'text-gray-400'} uppercase mb-2`}>EMAIL</p>
                <p className={`text-base ${theme === 'bubblegum' ? 'text-pink-800' : 'text-gray-300'}`}>deeptiwakchaure@gmail.com</p>
              </a>
              <a 
                href="https://github.com/Deeptiwakchaure"
                target="_blank"
                rel="noopener noreferrer"
                className={`contact-card p-6 border ${theme === 'bubblegum' ? 'border-pink-400' : 'border-gray-700'} hover:border-white rounded-xl transition-all duration-300 group animate-fade-in-up delay-600 soft-shadow micro-interaction`}
              >
                <Github className={`w-8 h-8 mb-4 ${theme === 'bubblegum' ? 'text-pink-700' : 'text-gray-400'} group-hover:scale-110 transition-transform duration-300`} />
                <p className={`text-sm ${theme === 'bubblegum' ? 'text-pink-700' : 'text-gray-400'} uppercase mb-2`}>GITHUB</p>
                <p className={`text-base ${theme === 'bubblegum' ? 'text-pink-800' : 'text-gray-300'}`}>Connect with me</p>
              </a>
              <a 
                href="https://linkedin.com/in/deepti-wakchaure-657498230"
                target="_blank"
                rel="noopener noreferrer"
                className={`contact-card p-6 border ${theme === 'bubblegum' ? 'border-pink-400' : 'border-gray-700'} hover:border-white rounded-xl transition-all duration-300 group animate-fade-in-up delay-800 soft-shadow micro-interaction`}
              >
                <Linkedin className={`w-8 h-8 mb-4 ${theme === 'bubblegum' ? 'text-pink-700' : 'text-gray-400'} group-hover:scale-110 transition-transform duration-300`} />
                <p className={`text-sm ${theme === 'bubblegum' ? 'text-pink-700' : 'text-gray-400'} uppercase mb-2`}>LINKEDIN</p>
                <p className={`text-base ${theme === 'bubblegum' ? 'text-pink-800' : 'text-gray-300'}`}>Connect with me</p>
              </a>
              <a 
                href="https://dribbble.com/deeptiwakchaure"
                target="_blank"
                rel="noopener noreferrer"
                className={`contact-card p-6 border ${theme === 'bubblegum' ? 'border-pink-400' : 'border-gray-700'} hover:border-white rounded-xl transition-all duration-300 group animate-fade-in-up delay-1000 soft-shadow micro-interaction dribbble-card`}
              >
                <Dribbble className={`w-8 h-8 mb-4 dribbble-icon group-hover:scale-110 transition-transform duration-300`} />
                <p className={`text-sm ${theme === 'bubblegum' ? 'text-pink-700' : 'text-gray-400'} uppercase mb-2`}>DRIBBBLE</p>
                <p className={`text-base ${theme === 'bubblegum' ? 'text-pink-800' : 'text-gray-300'}`}>View my work</p>
              </a>
              <a 
                href="https://www.behance.net/deeptiwakchaure"
                target="_blank"
                rel="noopener noreferrer"
                className={`contact-card p-6 border ${theme === 'bubblegum' ? 'border-pink-400' : 'border-gray-700'} hover:border-white rounded-xl transition-all duration-300 group animate-fade-in-up delay-1200 soft-shadow micro-interaction behance-card`}
              >
                <ExternalLink className={`w-8 h-8 mb-4 behance-icon group-hover:scale-110 transition-transform duration-300`} />
                <p className={`text-sm ${theme === 'bubblegum' ? 'text-pink-700' : 'text-gray-400'} uppercase mb-2`}>BEHANCE</p>
                <p className={`text-base ${theme === 'bubblegum' ? 'text-pink-800' : 'text-gray-300'}`}>View my work</p>
              </a>
            </div>
            
            <div className="border-t border-gray-700 pt-8 animate-fade-in-up delay-1400">
              <p className={`text-xs ${theme === 'bubblegum' ? 'text-pink-600' : 'text-gray-500'}`}>
                © 2025 DEEPTI WAKCHAURE. ALL RIGHTS RESERVED.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;